import React, { useState } from 'react';
import { AlertTriangle, X, RotateCcw, Flag, ExternalLink, UserPlus, LogIn } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';

// Delete Confirmation Dialog
export const DeleteDialog = ({ open, itemName, itemType, onCancel, onConfirm }) => {
  if (!open) return null;
  
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center">
      <div className="bg-white rounded-xl p-6 max-w-md mx-4 shadow-2xl">
        <h3 className="text-lg font-bold mb-2">Confirmar Eliminación</h3>
        <p className="text-gray-600 mb-4">
          ¿Estás seguro de eliminar <strong>{itemName}</strong>? 
          {itemType && <span className="block text-sm text-red-600 mt-1">Tipo: {itemType}</span>}
        </p>
        <div className="flex gap-3 justify-end">
          <Button variant="outline" onClick={onCancel}>
            Cancelar
          </Button>
          <Button variant="destructive" onClick={onConfirm}>
            Eliminar
          </Button>
        </div>
      </div>
    </div>
  );
};

// Admin Action Confirmation Dialog
export const AdminActionDialog = ({ open, action, userName, onCancel, onConfirm }) => {
  if (!open) return null;
  
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center">
      <div className="bg-white rounded-xl p-6 max-w-md mx-4 shadow-2xl">
        <h3 className="text-lg font-bold mb-2">
          {action === 'promote' && 'Promover a Administrador'}
          {action === 'revoke' && 'Revocar Administrador'}
          {action === 'disable' && 'Deshabilitar Usuario'}
          {action === 'enable' && 'Habilitar Usuario'}
        </h3>
        <p className="text-gray-600 mb-4">
          {action === 'promote' && (
            <>¿Estás seguro de promover a <strong>{userName}</strong> como administrador? Tendrá acceso completo al panel de administración.</>
          )}
          {action === 'revoke' && (
            <>¿Estás seguro de revocar los privilegios de administrador de <strong>{userName}</strong>?</>
          )}
          {action === 'disable' && (
            <>¿Estás seguro de deshabilitar a <strong>{userName}</strong>? No podrá acceder a la plataforma y sus servicios/cursos no serán visibles.</>
          )}
          {action === 'enable' && (
            <>¿Estás seguro de habilitar a <strong>{userName}</strong>? Podrá volver a acceder a la plataforma.</>
          )}
        </p>
        <div className="flex gap-3 justify-end">
          <Button variant="outline" onClick={onCancel}>
            Cancelar
          </Button>
          <Button 
            className={action === 'promote' ? 'bg-green-500 hover:bg-green-600' : action === 'enable' ? 'bg-green-500 hover:bg-green-600' : 'bg-orange-500 hover:bg-orange-600'}
            onClick={onConfirm}
          >
            {action === 'promote' && 'Promover'}
            {action === 'revoke' && 'Revocar'}
            {action === 'disable' && 'Deshabilitar'}
            {action === 'enable' && 'Habilitar'}
          </Button>
        </div>
      </div>
    </div>
  );
};

// Report Review Confirmation Dialog
export const ReportReviewDialog = ({ open, outcome, reporterName, reportedName, onCancel, onConfirm }) => (
  <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onCancel()}>
    <DialogContent className="max-w-md">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          {outcome === 'infraction' ? (
            <>
              <AlertTriangle className="h-5 w-5 text-red-500" />
              Conferma Infrazione
            </>
          ) : (
            <>
              <X className="h-5 w-5 text-blue-500" />
              Conferma Segnalazione Nulla
            </>
          )}
        </DialogTitle>
      </DialogHeader>
      
      <div className="py-4">
        {outcome === 'infraction' ? (
          <div className="space-y-3">
            <p className="text-gray-600">
              Stai per confermare che <strong>{reportedName}</strong> ha commesso un'infrazione.
            </p>
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-red-700 text-sm font-medium">
                L'utente segnalato riceverà 1 punto negativo
              </p>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-gray-600">
              Stai per dichiarare che la segnalazione di <strong>{reporterName}</strong> è infondata.
            </p>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-blue-700 text-sm font-medium">
                Il segnalatore riceverà 1 punto negativo
              </p>
            </div>
          </div>
        )}
      </div>
      
      <div className="flex gap-3 justify-end">
        <Button variant="outline" onClick={onCancel}>
          Annulla
        </Button>
        <Button 
          variant={outcome === 'infraction' ? 'destructive' : 'default'}
          onClick={onConfirm}
          data-testid="confirm-report-review"
        >
          {outcome === 'infraction' ? 'Conferma Infrazione' : 'Conferma Segnalazione Nulla'}
        </Button>
      </div>
    </DialogContent>
  </Dialog>
);

// Reset Points Confirmation Dialog
export const ResetPointsDialog = ({ open, userName, onCancel, onConfirm }) => (
  <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onCancel()}>
    <DialogContent className="max-w-md">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <RotateCcw className="h-5 w-5 text-green-500" />
          Reset Punti Negativi
        </DialogTitle>
      </DialogHeader>
      
      <div className="py-4">
        <p className="text-gray-600">
          Vuoi resettare i punti negativi di <strong>{userName}</strong>?
        </p>
        <div className="bg-green-50 border border-green-200 rounded-lg p-3 mt-3">
          <p className="text-green-700 text-sm">
            I punti negativi verranno azzerati e l'utente potrà continuare ad utilizzare la piattaforma normalmente.
          </p>
        </div>
      </div>
      
      <div className="flex gap-3 justify-end">
        <Button variant="outline" onClick={onCancel}>
          Annulla
        </Button>
        <Button onClick={onConfirm} className="bg-green-600 hover:bg-green-700">
          Conferma Reset
        </Button>
      </div>
    </DialogContent>
  </Dialog>
);

// Report Detail Dialog
export const ReportDetailDialog = ({ open, report, onClose, onReviewInfraction, onReviewNullReport }) => (
  <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
    <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
      {report && (
        <>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Flag className="h-5 w-5 text-red-500" />
              Dettaglio Segnalazione
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Status Banner */}
            <div className={`p-4 rounded-lg ${
              report.status === 'pending' 
                ? 'bg-yellow-50 border border-yellow-200' 
                : report.outcome === 'infraction'
                  ? 'bg-red-50 border border-red-200'
                  : 'bg-blue-50 border border-blue-200'
            }`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-semibold">
                    {report.status === 'pending' ? 'Da verificare' : 
                     report.outcome === 'infraction' ? 'Infrazione confermata' : 'Segnalazione nulla'}
                  </p>
                  <p className="text-sm text-gray-600">
                    Creata il {new Date(report.created_at).toLocaleDateString('it-IT', { 
                      day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' 
                    })}
                  </p>
                </div>
                {report.status === 'verified' && report.reviewed_at && (
                  <p className="text-sm text-gray-500">
                    Verificata il {new Date(report.reviewed_at).toLocaleDateString('it-IT')}
                  </p>
                )}
              </div>
            </div>

            {/* Reporter & Reported */}
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="text-xs text-gray-500 uppercase tracking-wide mb-2">Segnalatore</p>
                <p className="font-semibold">{report.reporter_name}</p>
                <p className="text-sm text-gray-600">{report.reporter_email}</p>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="text-xs text-gray-500 uppercase tracking-wide mb-2">Utente Segnalato</p>
                <p className="font-semibold">{report.reported_user_name}</p>
                <p className="text-sm text-gray-600">{report.reported_user_email}</p>
                {report.reported_user_negative_points > 0 && (
                  <Badge variant="destructive" className="mt-2">
                    {report.reported_user_negative_points}/3 punti negativi
                  </Badge>
                )}
              </div>
            </div>

            {/* Content Info */}
            <div className="p-4 bg-gray-50 rounded-lg">
              <p className="text-xs text-gray-500 uppercase tracking-wide mb-2">Contenuto Segnalato</p>
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-semibold">{report.content_title || 'N/A'}</p>
                  <Badge variant="outline" className="mt-1">
                    {report.content_type === 'service' ? 'Servizio' : 'Corso'}
                  </Badge>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => window.open(`/${report.content_type}/${report.content_id}`, '_blank')}
                >
                  <ExternalLink className="h-4 w-4 mr-1" />
                  Visualizza
                </Button>
              </div>
            </div>

            {/* Report Type */}
            <div className="p-4 bg-gray-50 rounded-lg">
              <p className="text-xs text-gray-500 uppercase tracking-wide mb-2">Tipo di Problema</p>
              <p className="font-semibold">
                {{
                  'inappropriate_content': 'Contenuto inappropriato',
                  'false_information': 'Informazioni false/ingannevoli',
                  'spam': 'Spam/pubblicità',
                  'fake_profile': 'Profilo fake',
                  'unprofessional_behavior': 'Comportamento scorretto del professionista',
                  'other': 'Altro'
                }[report.report_type] || report.report_type}
              </p>
            </div>

            {/* Description */}
            {report.description && (
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-xs text-gray-500 uppercase tracking-wide mb-2">Descrizione del Segnalatore</p>
                <p className="text-gray-800 whitespace-pre-wrap">{report.description}</p>
              </div>
            )}

            {/* Actions for pending reports */}
            {report.status === 'pending' && (
              <div className="flex gap-3 pt-4 border-t">
                <Button
                  variant="destructive"
                  className="flex-1"
                  onClick={onReviewInfraction}
                >
                  <AlertTriangle className="h-4 w-4 mr-2" />
                  Conferma Infrazione
                </Button>
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={onReviewNullReport}
                >
                  <X className="h-4 w-4 mr-2" />
                  Segnalazione Nulla
                </Button>
              </div>
            )}
          </div>
        </>
      )}
    </DialogContent>
  </Dialog>
);


// Create User Dialog
export const CreateUserDialog = ({ open, onCancel, onConfirm, loading }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    preferred_language: 'es'
  });

  const handleSubmit = () => {
    if (!formData.name || !formData.email || !formData.password) {
      alert('Por favor completa todos los campos');
      return;
    }
    onConfirm(formData);
  };

  const resetForm = () => {
    setFormData({ name: '', email: '', password: '', preferred_language: 'es' });
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) { resetForm(); onCancel(); } }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="h-5 w-5 text-green-500" />
            Crear Nuevo Usuario
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nombre *</label>
            <Input
              placeholder="Nombre completo"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
            <Input
              type="email"
              placeholder="email@ejemplo.com"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Contraseña temporal *</label>
            <Input
              type="text"
              placeholder="Contraseña inicial"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
            />
            <p className="text-xs text-gray-500 mt-1">El usuario recibirá estas credenciales por email</p>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Idioma preferido</label>
            <Select value={formData.preferred_language} onValueChange={(v) => setFormData({ ...formData, preferred_language: v })}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="es">Español</SelectItem>
                <SelectItem value="en">English</SelectItem>
                <SelectItem value="it">Italiano</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        
        <div className="flex gap-3 justify-end">
          <Button variant="outline" onClick={() => { resetForm(); onCancel(); }}>
            Cancelar
          </Button>
          <Button onClick={handleSubmit} disabled={loading} className="bg-green-600 hover:bg-green-700">
            {loading ? 'Creando...' : 'Crear Usuario'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Impersonate Confirmation Dialog
export const ImpersonateDialog = ({ open, userName, onCancel, onConfirm }) => (
  <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onCancel()}>
    <DialogContent className="max-w-md">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <LogIn className="h-5 w-5 text-blue-500" />
          Impersonar Usuario
        </DialogTitle>
      </DialogHeader>
      
      <div className="py-4">
        <p className="text-gray-600">
          Estás a punto de impersonar a <strong>{userName}</strong>.
        </p>
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-3">
          <p className="text-blue-700 text-sm">
            Verás la aplicación como si fueras este usuario. Podrás crear perfiles, servicios y cursos en su nombre.
          </p>
        </div>
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mt-3">
          <p className="text-yellow-700 text-sm">
            ⚠️ El usuario recibirá una notificación por email de cualquier contenido que crees en su nombre.
          </p>
        </div>
      </div>
      
      <div className="flex gap-3 justify-end">
        <Button variant="outline" onClick={onCancel}>
          Cancelar
        </Button>
        <Button onClick={onConfirm} className="bg-blue-600 hover:bg-blue-700">
          <LogIn className="h-4 w-4 mr-2" />
          Impersonar
        </Button>
      </div>
    </DialogContent>
  </Dialog>
);
